/** Shop extension settings (GET/PUT /shop-ext): reviews, manual payment methods, gift cards, digital downloads. Settings tab. */
import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Empty, Input, InputNumber, Result, Row, Select, Space, Spin, Switch, Typography, message } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import { QUIET } from './api';
import { Hint, toastErr, useSite } from './common';

const { Text } = Typography;

/** shared loader for /shop-ext (legal page uses it too) */
export const useShopExt = () => {
    const { api } = useSite();
    const [s, setS] = useState(null);
    const [soon, setSoon] = useState(false);
    useEffect(() => {
        api.get('/shop-ext', null, QUIET).then(setS).catch((e) => { if (e?.status === 404) setSoon(true); else toastErr(e); setS({}); });
    }, [api]);
    const save = async (part) => {
        try { const r = await api.put('/shop-ext', part); setS(r); return r; } catch (e) { toastErr(e); return null; }
    };
    return { s, setS, soon, save };
};

const Row2 = ({ label, hint, children }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <div style={{ width: 260 }}><Text>{label}</Text>{hint && <Hint title={hint} />}</div>
        <div>{children}</div>
    </div>
);

const ShopExtSettings = () => {
    const { t } = useTranslation();
    const { s, soon, save } = useShopExt();
    const [rv, setRv] = useState({});
    const [gc, setGc] = useState({});
    const [dg, setDg] = useState({});
    const [mp, setMp] = useState([]);
    useEffect(() => {
        if (!s) return;
        setRv(s.reviews || {}); setGc(s.gift_cards || {}); setDg(s.digital || {}); setMp(s.manual_payments || []);
    }, [s]);

    if (soon) return <Result status="info" title={t('Coming soon')} />;
    if (!s) return <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>;
    const setM = (i, patch) => setMp((xs) => xs.map((x, j) => (j === i ? { ...x, ...patch } : x)));
    const ok = (r) => r && message.success(t('Saved'));
    return (
        <Space direction="vertical" size={16} style={{ width: '100%', maxWidth: 760 }}>
            <Card size="small" title={t('Product reviews')} extra={<Button size="small" type="primary" onClick={async () => ok(await save({ reviews: rv }))}>{t('Save')}</Button>}>
                <Row2 label={t('Enable reviews')}><Switch checked={!!rv.enabled} onChange={(v) => setRv({ ...rv, enabled: v })} /></Row2>
                <Row2 label={t('Only buyers can review')}><Switch checked={!!rv.require_purchase} onChange={(v) => setRv({ ...rv, require_purchase: v })} /></Row2>
                <Row2 label={t('Publish without moderation')}><Switch checked={!!rv.auto_publish} onChange={(v) => setRv({ ...rv, auto_publish: v })} /></Row2>
            </Card>
            <Card size="small" title={<span>{t('Manual payment methods')}<Hint title={t('Bank transfer, cash on delivery … Orders stay pending until you mark them paid.')} /></span>}
                extra={<Button size="small" type="primary" onClick={async () => ok(await save({ manual_payments: mp }))}>{t('Save')}</Button>}>
                {!mp.length && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('None')} />}
                {mp.map((m, i) => (
                    <div key={m.id || i} style={{ border: '1px solid var(--ant-color-border-secondary, #f0f0f0)', borderRadius: 8, padding: 10, marginBottom: 8 }}>
                        <Space wrap style={{ marginBottom: 6 }}>
                            <Select size="small" value={m.kind || 'other'} onChange={(kind) => setM(i, { kind })} style={{ width: 160 }}
                                options={[{ value: 'bank_transfer', label: t('Bank transfer') }, { value: 'cod', label: t('Cash on delivery') }, { value: 'other', label: t('Other') }]} />
                            <Input size="small" placeholder={t('Name')} value={m.name} onChange={(e) => setM(i, { name: e.target.value })} style={{ width: 220 }} />
                            <Switch size="small" checked={m.enabled !== false} onChange={(enabled) => setM(i, { enabled })} />
                            <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => setMp((xs) => xs.filter((_, j) => j !== i))} />
                        </Space>
                        <Input.TextArea size="small" autoSize={{ minRows: 2 }} placeholder={t('Instructions shown at checkout and in the order email')} value={m.instructions} onChange={(e) => setM(i, { instructions: e.target.value })} />
                    </div>
                ))}
                <Button size="small" type="dashed" icon={<PlusOutlined />} disabled={mp.length >= 10} onClick={() => setMp((xs) => [...xs, { kind: 'bank_transfer', name: '', instructions: '', enabled: true }])}>{t('Add method')}</Button>
            </Card>
            <Row gutter={16}>
                <Col xs={24} md={12}>
                    <Card size="small" title={t('Gift cards')} extra={<Button size="small" type="primary" onClick={async () => ok(await save({ gift_cards: gc }))}>{t('Save')}</Button>}>
                        <Space><Text>{t('Expire after')}</Text><InputNumber min={0} max={120} value={gc.expires_months} onChange={(v) => setGc({ ...gc, expires_months: v || 0 })} addonAfter={t('months')} style={{ width: 150 }} /><Hint title={t('0 = never')} /></Space>
                    </Card>
                </Col>
                <Col xs={24} md={12}>
                    <Card size="small" title={t('Digital downloads')} extra={<Button size="small" type="primary" onClick={async () => ok(await save({ digital: dg }))}>{t('Save')}</Button>}>
                        <Space direction="vertical">
                            <Space><Text style={{ width: 120, display: 'inline-block' }}>{t('Download limit')}</Text><InputNumber min={0} max={1000} value={dg.download_limit} onChange={(v) => setDg({ ...dg, download_limit: v || 0 })} /><Hint title={t('0 = unlimited')} /></Space>
                            <Space><Text style={{ width: 120, display: 'inline-block' }}>{t('Link valid for')}</Text><InputNumber min={0} max={3650} value={dg.link_days} onChange={(v) => setDg({ ...dg, link_days: v || 0 })} addonAfter={t('days')} style={{ width: 140 }} /></Space>
                        </Space>
                    </Card>
                </Col>
            </Row>
        </Space>
    );
};

export default ShopExtSettings;
